import React, { createContext, useContext, useEffect, useReducer } from 'react';
import API from '../api';

const CartContext = createContext();

const initialState = {
  items: [],
  subtotal: 0,
  loading: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'LOADING': return { ...state, loading: true, error: null };
    case 'SET_CART': return { ...state, loading: false, ...action.payload };
    case 'ERROR': return { ...state, loading: false, error: action.payload };
    default: return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchCart = async () => {
    dispatch({ type: 'LOADING' });
    try {
      const res = await API.get('/cart');
      dispatch({ type: 'SET_CART', payload: { items: res.data.items, subtotal: parseFloat(res.data.subtotal) }});
    } catch (err) {
      dispatch({ type: 'ERROR', payload: err.message });
    }
  };

  const addToCart = async (productId, quantity = 1, meta = null) => {
    try {
      await API.post('/cart/add', { product_id: productId, quantity, meta });
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  const updateItem = async (id, quantity) => {
    try {
      await API.put(`/cart/update/${id}`, { quantity });
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  const removeItem = async (id) => {
    try {
      await API.delete(`/cart/remove/${id}`);
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  const clearCart = async () => {
    try {
      await API.post('/cart/clear');
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  // Call this after user login to merge guest cart into user cart
  const mergeCart = async () => {
    try {
      const sessionId = localStorage.getItem('cart_session_id');
      if (!sessionId) return;
      await API.post('/cart/merge', { session_id: sessionId });
      // create a new session id so the old guest cart id isn't reused
      const { v4: uuidv4 } = await import('uuid');
      const newId = uuidv4();
      localStorage.setItem('cart_session_id', newId);
      await fetchCart();
    } catch (err) {
      console.error('Merge cart failed', err);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  return (
    <CartContext.Provider value={{
      ...state,
      fetchCart,
      addToCart,
      updateItem,
      removeItem,
      clearCart,
      mergeCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
